import dayjs from "dayjs";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useStore } from "react-redux";
import {
  setRangeMs,
  setTimeZone,
} from "store/slices/scrapTrackingViewportSlice";
import type { RootState } from "store/store";
import { useNavigationQuery } from "./NavigationQueryProvider";
import { choiceQuery } from "./queryCodecs";
import { useNavigationDateRange } from "./useNavigationDateRange";

const timezoneQuery = choiceQuery(["UTC", "local"] as const);
export const useNavigationViewport = (key: string, days: number) => {
  const store = useStore<RootState>();
  const { range, setRange, pickerValue, setPickerValue } =
    useNavigationDateRange(key, () => [
      dayjs.utc().subtract(days, "day"),
      dayjs.utc(),
    ]);
  const [timezone, setTimezone] = useNavigationQuery(
    "timezone",
    "UTC",
    timezoneQuery,
    "replace"
  );
  const applyingRef = useRef(false);
  const start = range[0]?.toDate() ?? new Date(0);
  const end = range[1]?.toDate() ?? new Date(0);
  const startMs = start.getTime();
  const endMs = end.getTime();
  useLayoutEffect(() => {
    applyingRef.current = true;
    store.dispatch(setRangeMs({ startMs, endMs }));
    store.dispatch(setTimeZone(timezone));
    applyingRef.current = false;
  }, [store, startMs, endMs, timezone]);
  useEffect(() => {
    let previous = store.getState().scrapTrackingViewport;
    return store.subscribe(() => {
      const viewport = store.getState().scrapTrackingViewport;
      const rangeChanged =
        viewport.startMs !== previous.startMs ||
        viewport.endMs !== previous.endMs;
      const timezoneChanged = viewport.timeZone !== previous.timeZone;
      previous = viewport;
      if (applyingRef.current) return;
      if (rangeChanged && viewport.startMs < viewport.endMs)
        setRange([dayjs.utc(viewport.startMs), dayjs.utc(viewport.endMs)]);
      if (timezoneChanged) setTimezone(viewport.timeZone);
    });
  }, [store, setRange, setTimezone]);
  return { start, end, pickerValue, handleRangeChange: setPickerValue };
};
